/* eslint-disable  @typescript-eslint/no-explicit-any */
import { EngageCountableKind, EngageCountableType } from '@/types/engage.types';
import { i18n_t as $t } from '@/utils/i18n';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import lowerCase from 'lodash/lowerCase';
import { sortData } from '@/common/multiselect-blade/multiselect-blade.utils.js';
import { getGroupColorByCSSClass, groupColorMap } from '@/groups/group-color-picker/group-color-service';

export type TargetBladeTargetType = EngageCountableType | 'anyActivity' | 'productArea';

type TargetOptionsType = {
    apps: boolean;
    events: boolean;
    guides: boolean;
    productArea: boolean;
};

type EventOptionsType = {
    pages: boolean;
    features: boolean;
    trackEvents: boolean;
    agentEvents: boolean;
};

type TargetFilterOption = {
    label: string;
    name?: 'anyActivity' | 'guideViews' | 'productArea';
    id: 'app' | 'event' | 'guide' | 'productArea';
    kind?: 'Guide' | Array<EngageCountableKind> | 'productArea' | 'anyActivity';
    icon?: { type: string };
};

type TargetEventTypeOption = {
    label: string;
    name: 'pageViews' | 'featureClicks' | 'trackEvents' | 'agentEvents';
    id: 'page' | 'feature' | 'trackType' | 'agentEvents';
    kind: EngageCountableKind | 'AIAgent' | 'AiAgent';
    icon?: { type: string };
};

/**
 * Retrieve the type name of a target. Useful for pulling type metadata from the type-specific constants mapping.
 *
 * Note that when Pages or Features are shared (an Adopt concept) they lose their "kind" field. As a result,
 * this utility is liable to fail for Adopt customers that use the Engage UI.
 */
export const getTargetType = (target: {
    kind?: EngageCountableKind;
    type?: TargetBladeTargetType;
}): TargetBladeTargetType => {
    const kindOrType = target?.kind || target?.type;
    switch (kindOrType) {
        case 'Page':
        case 'pageViews':
            return 'pageViews';
        case 'Feature':
        case 'featureClicks':
            return 'featureClicks';
        case 'TrackType':
        case 'trackEvents':
            return 'trackEvents';
        case 'Guide':
        case 'guideViews':
            return 'guideViews';
        case 'AIAgent':
        case 'AiAgent':
        case 'aiAgentInteractions':
            return 'aiAgentInteractions';
        case 'productArea':
            return 'productArea';
        default:
            return 'anyActivity';
    }
};

export const getFilterOptions = (): Record<'apps' | 'events' | 'guides' | 'productArea', TargetFilterOption> => {
    return {
        apps: {
            label: $t('Any activity (Apps)'),
            name: 'anyActivity',
            id: 'app',
            icon: { type: 'globe' },
            kind: 'anyActivity',
        },
        events: {
            label: $t('Events'),
            id: 'event',
            icon: { type: 'page' },
            kind: ['Page', 'Feature', 'TrackType', 'AIAgent'],
        },
        guides:{
            label: $t('Guides'),
            name: 'guideViews',
            id: 'guide',
            kind: 'Guide',
            icon: { type: 'guide' },
        },
        productArea:{
            label: $t('Product Areas'),
            name: 'productArea',
            id: 'productArea',
            icon: { type: 'layout' },
            kind: 'productArea',
        },
    };
};

export const getFilterTypeFromTargets = (targets: Array<{ kind?: EngageCountableKind; type?: TargetBladeTargetType }>): TargetFilterOption | undefined => {
    if (!targets || targets.length === 0) return undefined;

    const allFilterOptions = getFilterOptions();

    const firstTarget = targets[0];
    const kindOrType: EngageCountableKind | TargetBladeTargetType | string | undefined = firstTarget.kind || firstTarget.type;

    switch (kindOrType) {
        case 'Guide':
        case 'guideViews':
            return allFilterOptions.guides;
        case 'Page':
        case 'Feature':
        case 'TrackType':
        case 'pageViews':
        case 'featureClicks':
        case 'trackEvents':
        case 'AIAgent':
        case 'AiAgent':
        case 'aiAgentInteractions':
            return allFilterOptions.events;
        case 'productArea':
            return allFilterOptions.productArea;
        case 'App':
        case 'anyActivity':
            return allFilterOptions.apps;
    }

    return undefined;
};

// Deprecated- use getFilterOptions function instead.
export const FILTER_OPTIONS: Array<TargetFilterOption> = [
    {
        label: 'Any activity (Apps)',
        name: 'anyActivity',
        id: 'app',
        icon: { type: 'globe' },
        kind: 'anyActivity',
    },
    {
        label: 'Events',
        id: 'event',
        icon: { type: 'page' },
        kind: ['Page', 'Feature', 'TrackType', 'AIAgent'],
    },
    {
        label: 'Guides',
        name: 'guideViews',
        id: 'guide',
        kind: 'Guide',
        icon: { type: 'guide' },
    },
    {
        label: 'Areas',
        name: 'productArea',
        id: 'productArea',
        icon: { type: 'layout' },
        kind: 'productArea',
    },
];

export const EVENT_TYPE_OPTIONS: Array<TargetEventTypeOption> = [
    {
        label: 'Page views',
        name: 'pageViews',
        id: 'page',
        kind: 'Page',
    },
    {
        label: 'Feature clicks',
        name: 'featureClicks',
        id: 'feature',
        kind: 'Feature',
    },
    {
        label: 'Track Events',
        name: 'trackEvents',
        id: 'trackType',
        kind: 'TrackType',
    },
    {
        label: 'AI agents',
        name: 'agentEvents',
        id: 'agentEvents',
        kind: 'AIAgent',
        icon: {
            type: 'agentic',
        },
    },
];

export const EXPANDED_ID_PLATFORM_APPS = [
    {
        displayName: 'All Apps',
        id: 'expandAppIds("*")',
        name: 'All Apps',
        kind: 'anyActivity',
    },
    {
        displayName: 'Web Apps',
        id: 'expandAppIds("web")',
        name: 'Web Apps',
        kind: 'anyActivity',
    },
    {
        displayName: 'Mobile Apps',
        id: 'expandAppIds("mobile")',
        name: 'Mobile Apps',
        kind: 'anyActivity',
    },
    {
        displayName: 'Extension Apps',
        id: 'expandAppIds("extension")',
        name: 'Extension Apps',
        kind: 'anyActivity',
    },
];

export const FORMATTED_PLATFORM_APPS = [
    {
        displayName: 'All Apps',
        id: '*',
        name: 'All Apps',
        kind: 'anyActivity',
    },
    {
        displayName: 'Web Apps',
        id: 'web',
        name: 'Web Apps',
        kind: 'anyActivity',
    },
    {
        displayName: 'Mobile Apps',
        id: 'mobile',
        name: 'Mobile Apps',
        kind: 'anyActivity',
    },
    {
        displayName: 'Extension Apps',
        id: 'extension',
        name: 'Extension Apps',
        kind: 'anyActivity',
    },
];

export const mapFullProductAreasListToMiniList = (productAreasList: Array<any>) => {
    if (!productAreasList) return null;

    const pickedProductAreas = productAreasList.map((el) => {
        return {
            kind: 'productArea',
            id: el.id,
            name: el.name,
            icon: {
                type: 'square',
                fill: getGroupColorByCSSClass(el.color),
                stroke: getGroupColorByCSSClass(el.color),
                size: 12,
            },
        };
    });

    return Object.freeze(pickedProductAreas);
};

export const mapStarredValueToEventOrGuide = (eventOrGuideList: Array<any>, getStarByItemId: (id: string) => any) => {
    if (!eventOrGuideList || !getStarByItemId) return [];

    const compactEventOrGuideList: Array<any> = [];

    eventOrGuideList.forEach((eventOrGuide) => {
        if (eventOrGuide && eventOrGuide.id) {
            compactEventOrGuideList.push({
                ...eventOrGuide,
                isStarred: !!getStarByItemId(eventOrGuide.id),
            });
        }
    });

    return compactEventOrGuideList;
};

export const sortRecentEventsList = (recentEventsList: Array<any>) => {
    if (!recentEventsList) return null;

    recentEventsList.sort((a, b) => b.lastUsed - a.lastUsed);

    return recentEventsList;
};

export const mapFullObjectToSelectedTargetObject = (
    fullTargetObject: { id: string; kind?: EngageCountableKind; type?: TargetBladeTargetType },
    additionalProps: Record<string, any> = {},
) => {
    if (!fullTargetObject || !fullTargetObject.id) return null;

    const targetType = getTargetType(fullTargetObject);

    return {
        ...additionalProps,
        id: fullTargetObject.id,
        type: targetType,
    };
};

export const getEventOptions = (eventOptions: EventOptionsType) => {
    const EVENT_TYPES: Array<TargetEventTypeOption> = [
        {
            label: $t('Page views'),
            name: 'pageViews',
            id: 'page',
            kind: 'Page',
        },
        {
            label: $t('Feature clicks'),
            name: 'featureClicks',
            id: 'feature',
            kind: 'Feature',
        },
        {
            label: $t('Track Events'),
            name: 'trackEvents',
            id: 'trackType',
            kind: 'TrackType',
        },
        {
            label: $t('AI agent prompts'),
            name: 'agentEvents',
            id: 'agentEvents',
            kind: 'AIAgent',
            icon: {
                type: 'agentic',
            },
        },
    ];
    const eventsOptions = [];
    if (eventOptions.pages) {
        eventsOptions.push(EVENT_TYPES[0]);
    }

    if (eventOptions.features) {
        eventsOptions.push(EVENT_TYPES[1]);
    }

    if (eventOptions.trackEvents) {
        eventsOptions.push(EVENT_TYPES[2]);
    }

    if (eventOptions.agentEvents) {
        eventsOptions.push(EVENT_TYPES[3]);
    }

    return eventsOptions;
};

export const getTargetOptions = (targetOptions: TargetOptionsType) => {
    const filterOptions = [];
    const allFilterOptions = getFilterOptions();
    if (targetOptions.apps) {
        filterOptions.push(allFilterOptions.apps);
    }

    if (targetOptions.productArea) {
        filterOptions.push(allFilterOptions.productArea);
    }

    if (targetOptions.events) {
        filterOptions.push(allFilterOptions.events);
    }

    if (targetOptions.guides) {
        filterOptions.push(allFilterOptions.guides);
    }

    return filterOptions;
};

export const getDefaultSelection = (defaultSelection: 'apps' | 'guides' | 'productArea' | 'events') => {
    const allFilterOptions = getFilterOptions();

    if (allFilterOptions[defaultSelection]) {
        return allFilterOptions[defaultSelection];
    }

    return allFilterOptions.events;
};

export const createGroupedEventTargetOptions = (options: Array<Record<string, any>>, appsById: Record<string, any>) => {
    const groupsMap: Record<string, any> = {
        noProductArea: {
            id: 'noProductArea',
            name: 'noProductArea',
            section: 'No Product Area',
            color: groupColorMap['.groupColorUngrouped'],
            targets: [],
        },
    };
    for (const option of options) {
        const optionCopy = cloneDeep(option);
        let targetArray: Array<any> = [];

        if (!optionCopy.group?.id) {
            targetArray = groupsMap.noProductArea.targets;
        } else if (!groupsMap[optionCopy.group.id]) {
            targetArray = [];
            groupsMap[optionCopy.group.id] = {
                id: optionCopy.group.id,
                name: optionCopy.group.id,
                section: optionCopy.group.name,
                color: getGroupColorByCSSClass(optionCopy.group.color),
                targets: targetArray,
            };
        } else {
            targetArray = groupsMap[optionCopy.group.id].targets;
        }

        if (optionCopy.appId && appsById[optionCopy.appId]) {
            optionCopy.appDisplayName = appsById[optionCopy.appId].displayName;
        }

        targetArray.push(Object.freeze(optionCopy));
    }

    const groupsArray = Object.values(groupsMap);

    return groupsArray;
};

export const createGroupedGuideTargetOptions = ({
    guideStatusOptions,
    allGuides,
    appsById,
}: {
    guideStatusOptions: Record<string, any>;
    allGuides: Array<Record<string, any>> | null;
    appsById: Record<string, any>;
}) => {
    if (!allGuides) return [];

    const guidesInApp = allGuides.map((guide) => {
        const guideCopy = cloneDeep(guide);
        if (guideCopy.appId && appsById[guideCopy.appId]) {
            guideCopy.appDisplayName = appsById[guideCopy.appId].displayName;
        }

        return Object.freeze(guideCopy);
    });

    const groupsMap: Record<string, any> = {};
    guideStatusOptions.forEach((status: Record<string, any>, index: number) => {
        groupsMap[status.id] = {
            id: status.id,
            name: status.name,
            section: status.name,
            color: status.icon.fill,
            targets: [...guidesInApp.filter((option) => option.state === status.id)],
            sortKey: index,
        };
    });

    const groupsArray = Object.values(groupsMap);

    return groupsArray;
};

export const createProductAreaTargetOptions = (targetOptions: Array<Record<string, any>>) => {
    return targetOptions
        .map((group) => {
            return {
                name: group.section,
                id: group.id,
                icon: {
                    type: 'square',
                    fill: group.color,
                    stroke: group.color,
                    size: 12,
                },
            };
        })
        .sort(sortData('name'));
};

export const createGroupedEventTargetOption = (event: Record<string, any>, appsById: Record<string, any>) => {
    const eventCopy = cloneDeep(event);
    const pickedTargetBase = pick(eventCopy, [
        'id',
        'name',
        'appDisplayName',
        'group',
        'appId',
        'kind',
        'dirty',
        'isCoreEvent',
        'appIcon',
        'icon',
        'type',
        'size',
    ]);
    if (pickedTargetBase.appId && appsById[pickedTargetBase.appId]) {
        pickedTargetBase.appDisplayName = appsById[pickedTargetBase.appId].displayName;
        pickedTargetBase.appIcon = appsById[pickedTargetBase.appId].icon;
    }
    pickedTargetBase.icon = {
        type: pickedTargetBase.kind === 'TrackType' ? 'zap' : lowerCase(pickedTargetBase.kind),
    };
    pickedTargetBase.type = 'option';
    pickedTargetBase.size = 52;

    return Object.freeze(pickedTargetBase);
};
